import type { PushAttentionReason } from '#features/notifications/utils/pushAttention';
import { tracking } from './tracking';

/**
 * Воронка разговора об уведомлениях: «понадобилось спросить → показали → человек ответил».
 *
 * Заведена потому, что спор о шторке разрешать было нечем. Три дня подряд решения принимались
 * по ощущениям: сколько людей её видят, сколько соглашаются, сколько закрывают «Позже» — не знал
 * никто, и единственным фактом было косвенное «139 токенов при ~280 активных устройствах».
 *
 * Считаем и молчаливую ветку тоже: с 13.5 большинство телефонов проходит её вообще без экрана,
 * и без этой отметки успех выглядел бы как отсутствие событий.
 */
const CATEGORY = 'push_permission';

/** Итог тихой попытки забрать токен, когда система уже всё разрешила. */
export type SilentOutcome =
  /** Токен взят молча, человек ничего не увидел. Нормальный, самый частый исход. */
  | 'taken'
  /** Разрешения нет или прочитать не удалось — молча тут ничего не сделать. */
  | 'not-allowed'
  /** Попытка была и не удалась: FCM промолчал, сеть, отказ сервера. Дальше говорит шторка. */
  | 'failed';

/** Шторка показана. label — причина, по которой мы заговорили. */
export const trackPushSheetView = (reason: PushAttentionReason) =>
  tracking.event({ action: 'push_sheet_view', category: CATEGORY, label: reason });

/**
 * Нажата главная кнопка: «Включить» (поднимет системный диалог) или «Открыть настройки».
 * Само согласие ОС здесь не видно — его отражает следующая регистрация токена.
 */
export const trackPushSheetAction = (reason: PushAttentionReason) =>
  tracking.event({ action: 'push_sheet_action', category: CATEGORY, label: reason });

/** «Позже». Ответ, а не ошибка: по нему мы молчим неделю или месяц, смотря о чём спрашивали. */
export const trackPushSheetLater = (reason: PushAttentionReason) =>
  tracking.event({ action: 'push_sheet_later', category: CATEGORY, label: reason });

/** Чем закончилась тихая ветка — та, где человек не видит ничего. */
export const trackPushSilentRegistration = (outcome: SilentOutcome) =>
  tracking.event({ action: 'push_silent_registration', category: CATEGORY, label: outcome });

/*
 * Веб-воронка. Категория общая, действия свои.
 *
 * Складывать её с нативной нельзя: знаменатели разные (в приложении спрашивают у того, кто уже
 * поставил приложение, в вебе у случайного посетителя), а разделить их постфактум не выйдет,
 * потому что нативная оболочка это тот же WebView против того же прод-API, и GA видит её как
 * обычный браузер. Отдельные имена ещё и сохраняют смысл нативного ряда, который считается
 * с 03.09.2026.
 */

/** Шторка показана. */
export const trackWebPushPromptView = (reason: PushAttentionReason) =>
  tracking.event({ action: 'web_push_prompt_view', category: CATEGORY, label: reason });

/** Нажата главная кнопка. Сразу за ней поднимается диалог браузера. */
export const trackWebPushPromptAllow = (reason: PushAttentionReason) =>
  tracking.event({ action: 'web_push_prompt_allow', category: CATEGORY, label: reason });

/** «Не сейчас». Ответ, а не ошибка: по нему мы молчим неделю. */
export const trackWebPushPromptLater = (reason: PushAttentionReason) =>
  tracking.event({ action: 'web_push_prompt_later', category: CATEGORY, label: reason });

/** Крестик, Escape, клик мимо. Считается отдельно от «Не сейчас»: это разные ответы. */
export const trackWebPushPromptDismiss = (reason: PushAttentionReason) =>
  tracking.event({ action: 'web_push_prompt_dismiss', category: CATEGORY, label: reason });

/**
 * Что ответил браузерный диалог. Число, по которому и будет видно, сработала ли правка.
 *
 * Сегодня ответ браузера не записывается нигде, и единственным приближением была доля новых
 * токенов, которая путает отказ человека с молча упавшим service worker.
 *
 * Метка 'default' стоит отдельного внимания: это единственный сигнал о ловушке с жестом
 * пользователя. Если кто-нибудь однажды поставит await перед `Notification.requestPermission()`,
 * в Chrome всё продолжит работать, а в Firefox и Safari диалог просто перестанет появляться, и
 * узнаем мы об этом только по всплеску 'default'.
 */
export const trackWebPushDialogOutcome = (outcome: NotificationPermission) =>
  tracking.event({ action: 'web_push_dialog_outcome', category: CATEGORY, label: outcome });
